import { Controller } from "@hotwired/stimulus";

const TYPEAHEAD_DEBOUNCE_MS = 250;
let instanceCount = 0;

export default class extends Controller {
  static targets = ['input', 'template']
  static values = {
    url: String,
    wildcard: String,
    display: String,
    grouped: Boolean,
    limit: Number,
    searchGroup: Array,
    groupedNoTitle: Boolean,
    groupedTemplateId: String,
    quoteGroupId: String,
    biospecimensEnabled: Boolean,
    productsEnabled: Boolean,
    articlesEnabled: Boolean,
  }

  connect() {
    this.eventNamespace = `.typeaheadController${++instanceCount}`
    this.groupedSourceStates = new Map()
    this.buildSource()

    this.typeaheadInstance = $(this.inputTarget).typeahead(
      { highlight: true, minLength: 3, hint: false },
      this.buildDataset()
    )

    Handlebars.registerHelper("truncateText", this.truncateText);
    Handlebars.registerHelper("ifNotAll", function(filter, options) {
      if (filter !== "All Categories") {
        return options.fn(this)
      } else {
        return options.inverse(this)
      }
    })

    // typeahead.js cancels a dataset without telling its source (e.g. the query drops below minLength).
    $(this.inputTarget).on(`typeahead:asynccancel${this.eventNamespace}`, (_event, _query, dataset) => {
const state = this.groupedSourceStates.get(typeof dataset === 'string' ? dataset : dataset?.name);
      if (state) this.cancelGroupedSource(state);
    });

    // Intercept clicks on supplier selection suggestions
    $(document).on(`click${this.eventNamespace}`, '.tt-suggestion[data-supplier-id]', (e) => {
      const suggestion = e.target.closest('.tt-suggestion');
      const addButton = e.target.closest('.supplier-add-button[data-action-button="true"]');

      e.preventDefault();
      e.stopPropagation();

      if (addButton && suggestion.getAttribute('data-supplier-selected') !== 'true') {
        // Construct supplier data from DOM attributes
        const supplierData = {
          type: 'supplier_selection',
          id: suggestion.getAttribute('data-supplier-id'),
          name: suggestion.querySelector('.supplier-info-area strong')?.textContent,
          selected: false,
          supplier_selection_url: suggestion.getAttribute('data-supplier-url'),
          method: suggestion.getAttribute('data-supplier-method'),
          quote_group: suggestion.getAttribute('data-supplier-quote-group')
        };
        this.handleSupplierSelection(supplierData);
      } else {
        // Fill search box with supplier name
        this.inputTarget.value = suggestion.querySelector('.supplier-info-area strong')?.textContent;
      }
      this.close();
    });

    $(this.element).on(`typeahead:select${this.eventNamespace}`, (event, obj) => {
      // Skip supplier_selection - we handle it manually now
      if (obj.type && obj.type === 'supplier_selection') {
        return false;
      }

      this.handleSelection(obj)
    })

    // Update UI for already-added suppliers when typeahead renders
    $(this.element).on(`typeahead:render${this.eventNamespace} typeahead:asyncreceive${this.eventNamespace}`, () => {
      setTimeout(() => this.updateAlreadySelectedSuppliers(), 0);
    });

    // Listen for supplier add/remove via Rails UJS
    $(document).on(`ajax:success${this.eventNamespace}`, '.add-supplier', (e) => {
      const supplierId = e.currentTarget.closest('[data-uuid]')?.getAttribute('data-uuid');
      if (supplierId) {
        this.addedSupplierIds = this.addedSupplierIds || new Set();
        this.addedSupplierIds.add(supplierId);
        this.updateSupplierUI(supplierId, true);
      }
    });

    $(document).on(`ajax:success${this.eventNamespace}`, '.cancel-supplier, .remove-selection', (e) => {
      const supplierId = e.currentTarget.closest('[data-uuid]')?.getAttribute('data-uuid');
      if (supplierId) this.handleSupplierRemoval(supplierId);
    });
  }

  disconnect() {
    this.groupedSourceStates.forEach(state => this.cancelGroupedSource(state));
    this.groupedSourceStates.clear();
    $(document).off(this.eventNamespace);
    $(this.element).off(this.eventNamespace);
    $(this.inputTarget).off(this.eventNamespace).typeahead('destroy');
    this.typeaheadInstance = null;
  }

  close() {
    if (this.typeaheadInstance && this.typeaheadInstance.length > 0) {
      this.typeaheadInstance.typeahead('close');
    } else {
      $(this.inputTarget).blur();
      $('.tt-dropdown-menu').hide();
      $(this.inputTarget).removeClass('tt-open');
    }
  }

  updateAlreadySelectedSuppliers() {
    if (!this.addedSupplierIds?.size) return;
    this.addedSupplierIds.forEach(id => {
      document.querySelectorAll(`.tt-suggestion[data-supplier-id="${id}"]`).forEach(suggestion => {
        suggestion.setAttribute('data-supplier-selected', 'true');
        suggestion.classList.add('disabled');
        const button = suggestion.querySelector('.supplier-add-button');
        if (button) {
          button.replaceWith(this.buildSupplierButton(true));
        }
      });
    });
  }

  truncateText(text, length) {
    if (!text) return "";
  
    return text.length > length
      ? text.substring(0, length) + "..."
      : text;
  }

  removeItalic() {
    this.inputTarget.style.fontStyle = "normal";
  }

  buildSource() {
    let remote = { url: this.urlValue }
    if (this.wildcardValue !== '') {
      remote.wildcard = this.wildcardValue
    } else if (this.element.tagName === 'FORM') {
      remote.prepare = this.prepare.bind(this)
    }
    this.bloodhound = new Bloodhound({
      datumTokenizer: Bloodhound.tokenizers.whitespace,
      queryTokenizer: Bloodhound.tokenizers.whitespace,
      remote
    })
  }

  getGroupedActiveDatasets() {
    const datasets = [
      { name: "previous_searches", title: "Previous Searches", display: "match" },
      { name: "suggested_searches", title: "Suggested Searches" },
      { name: "suggested_categories", title: "Suggested Categories", display: "name", source: this.suggestedCategoriesSource.bind(this), limit: 10 },
      { name: "suppliers", title: "Request Custom Services with a Supplier", display: "name" },
      { name: "services", title: "Request Services", display: "name" },
    ];

    if (window.location.href.includes("/providers")) {
      datasets.push({ name: "supplier_selection", title: "", display: "name" });
    }
    
    return this.hasSearchGroupValue && this.searchGroupValue.length ? datasets.filter(ds => this.searchGroupValue.includes(ds.name)) : datasets;
  }

  buildGroupedDatasets(groupedDatasets) {
    if (!groupedDatasets.length) return [];

    const titleTemplate = this.groupedNoTitleValue ? '' : document.getElementById('typeahead-grouped-title-template').innerHTML;
    return groupedDatasets.map(dataset => ({
      ...dataset,
      source: dataset.source || this.buildGroupedSource(dataset.name),
      templates: {
        header: this.groupedNoTitleValue ? '' : Handlebars.compile(titleTemplate)({ title: dataset.title || dataset.name.replace(/_/g, ' ') }),
        suggestion: Handlebars.compile(document.getElementById(`${dataset.name}-template`).innerHTML),
      },
    }));
  }

  buildDataset() {
    let template;
    if (this.hasTemplateTarget) {
      template = Handlebars.compile(this.templateTarget.innerHTML);
    }

    if (this.hasGroupedValue && this.groupedValue) {
      const activeDatasets = this.getGroupedActiveDatasets();
      return this.buildGroupedDatasets(activeDatasets);
    } else {
      let dataset = { source: this.bloodhound };
      if (this.hasDisplayValue && this.displayValue !== "") {
        dataset.display = this.displayValue;
      }
      if (template) {
        dataset.templates = { suggestion: template };
      }
      if (this.hasLimitValue && this.limitValue !== "") {
        dataset.limit = this.limitValue;
      }
      return [dataset];
    }
  }

  // typeahead.js 0.11 only treats a source as async if it declares three params,
  // so keep the explicit signature rather than wrapping it in a generic debounce.
  buildGroupedSource(type) {
    const state = { timer: null, xhr: null, asyncResults: null };
    this.groupedSourceStates.set(type, state);

    return (query, syncResults, asyncResults) => {
      this.cancelGroupedSource(state);
      state.asyncResults = asyncResults;

      state.timer = setTimeout(() => {
        state.timer = null;
        const searchType = new URLSearchParams(window.location.search).get('search_type') || 'all';
        const url = `${this.urlValue}?query=${encodeURIComponent(query)}&search_type=${encodeURIComponent(searchType)}&group=${type}${ this.quoteGroupIdValue ? `&quote_group=${this.quoteGroupIdValue}` : '' }`;
        const xhr = $.getJSON(url, (data) => {
          if (state.xhr !== xhr) return;
          state.xhr = null;
          state.asyncResults = null;
          asyncResults(data[type] || []);
        });
        state.xhr = xhr;
      }, TYPEAHEAD_DEBOUNCE_MS);
    };
  }

  // Settles the superseded query's callback so typeahead never waits on it.
  cancelGroupedSource(state) {
    clearTimeout(state.timer);
    state.timer = null;
    state.xhr?.abort();
    state.xhr = null;
    const asyncResults = state.asyncResults;
    state.asyncResults = null;
    asyncResults?.([]);
  }

  handleSupplierSelection(supplier) {
    if (!supplier?.id || supplier.selected) return;

    this.pendingSupplierIds = this.pendingSupplierIds || new Set();
    this.addedSupplierIds = this.addedSupplierIds || new Set();

    if (this.addedSupplierIds.has(supplier.id) || this.pendingSupplierIds.has(supplier.id)) return;

    this.pendingSupplierIds.add(supplier.id);
    this.addedSupplierIds.add(supplier.id);

    this.updateSupplierUI(supplier.id, true);
    this.htmxSelectSupplier(supplier);
    setTimeout(() => this.pendingSupplierIds?.delete(supplier.id), 2000);
  }

  handleSupplierRemoval(supplierId) {
    if (!supplierId) return;

    this.addedSupplierIds?.delete(supplierId);
    this.pendingSupplierIds?.delete(supplierId);

    // Update UI to show supplier as available again
    this.updateSupplierUI(supplierId, false);
  }

  updateSupplierUI(supplierId, isSelected) {
    document.querySelectorAll(`.tt-suggestion[data-supplier-id="${supplierId}"]`).forEach(suggestion => {
      suggestion.setAttribute('data-supplier-selected', isSelected);
      suggestion.classList.toggle('disabled', isSelected);

      const container = suggestion.querySelector('.supplier-add-button, .supplier-already-selected')?.parentElement;
      if (container) {
        container
          .querySelector('.supplier-add-button, .supplier-already-selected')
          .replaceWith(this.buildSupplierButton(isSelected, supplierId));
      }
    });
  }

  // Keep in sync with the Handlebars markup in searches/_typeahead_grouped_templates.
  buildSupplierButton(isSelected, supplierId) {
    const span = document.createElement('span');

    if (isSelected) {
      span.className = 'supplier-already-selected text-muted';
      span.append('Already selected ', this.iconNode('typeahead-check-icon'));
    } else {
      span.className = 'supplier-add-button t-link fw-semibold';
      span.dataset.actionButton = 'true';
      span.dataset.supplierActionId = supplierId;
      span.append('Add to selected ', this.iconNode('typeahead-arrow-right-icon'));
    }

    return span;
  }

  iconNode(templateId) {
    const template = document.getElementById(templateId);
    return template?.content.firstElementChild?.cloneNode(true) || document.createTextNode('');
  }

  htmxSelectSupplier(supplier) {
    if (!supplier || !supplier.id) {
      return;
    }

    if (supplier.quote_group) {
      $.ajax({
        url: supplier.supplier_selection_url,
        method: supplier.method,
        dataType: 'script'
      });
    } else {
      RX.providers.htmxSelectSupplier(supplier);
    }
  }

  handleSelection(obj) {
    if (obj.url) {
      window.location.href = obj.url;
    } else {
      this.dispatch('selected', { detail: { selectionObj: obj } });
    }
  }

  prepare(query, settings) {
    settings.url = this.buildURL(query)
    return settings
  }

  get form() {
    return this.element
  }

  buildURL(query) {
    let data = new FormData(this.form);
    data.delete("authenticity_token")
    let urlParams = new URLSearchParams(data)
    if (urlParams.get('search_type') === '') {
      urlParams.delete('search_type')
    }
    urlParams.set('q', query)
    return `${this.urlValue}?${urlParams.toString()}`
  }

  suggestedCategoriesSource(query, syncResults) {
    const searchTypes = ["All Categories", "Suppliers", "Services", "Products", "Biospecimens", "Articles"]
      .filter(type => {
        if (type === "Products") return this.hasProductsEnabledValue && this.productsEnabledValue;
        if (type === "Biospecimens") return this.hasBiospecimensEnabledValue && this.biospecimensEnabledValue;
        if (type === "Articles") return this.hasArticlesEnabledValue && this.articlesEnabledValue;
        return true;
      });

    const results = searchTypes.map((filter) => ({
      name: query,
      filter: filter,
      url: `search?query=${encodeURIComponent(query)}&search_type=${filter.toLowerCase().replace(/\s+/g, '_')}`
    }));
    syncResults(results);
  }
}
